/* global Faye */

import Vue from "vue"
import App from "@/App"
import router from "@/router"
import createStore from "@/store"
import Vuetify from "vuetify"
import { provider } from "@/provider.js"
import { sync } from "vuex-router-sync"
import cfg from "@/cfg"
import Vuebar from "vuebar"
import wysiwyg from "vue-wysiwyg"
import currency from "v-currency-field"
import VueInputAutoWidth from "vue-input-autowidth"
import * as Sentry from "@sentry/vue"

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import "@/lib/fontAwesomeLibrary"

import Helpers from "@/helpers"
import Verte from "verte"
import "verte/dist/verte.css"
import "@/assets/styles/custom.css"

const LOADING_MESSAGES = {
  generic: "An unexpected error occurred while loading Caseblocks. Please refresh the page or contact support.",
  network: "Unable to connect to the Caseblocks API. Please make sure the API server is running and then refresh this page."
}

const GRAPHQL_HEALTHCHECK_URL = `${Helpers.removingTrailingSlash(cfg.apiEndPoint())}/graphql`
const API_HEALTHCHECK_INTERVAL_MS = 5000
let apiHealthCheckIntervalId = null

const normalizeOAuthDeepLinkForHashRouter = () => {
  if (typeof window === "undefined") {
    return
  }
  const pathname = window.location.pathname || ""
  const search = window.location.search || ""
  const hash = window.location.hash || ""
  const oauthPath = pathname.replace(/^\/xg/, "")
  const isOauthPath = oauthPath === "/oauth/authorize" || oauthPath === "/oauth/mcp/authorize"
  const canonicalHashPath = `#${oauthPath}`
  const hashRoute = hash.replace(/^#/, "")
  const hashPointsToOauth =
    hashRoute === oauthPath || hashRoute.indexOf(`${oauthPath}?`) === 0

  if (isOauthPath && !hashPointsToOauth) {
    const canonicalUrl = `/xg/#${oauthPath}${search}`
    if (window.location.href.indexOf(canonicalUrl) === -1) {
      console.warn("[OAuthDebug][Main.normalizeOAuthDeepLinkForHashRouter] redirecting to hash route", {
        from: window.location.href,
        to: canonicalUrl,
        hash,
        canonicalHashPath,
      })
      window.location.replace(canonicalUrl)
    }
  }
}

normalizeOAuthDeepLinkForHashRouter()

const getLoadingElements = () => {
  if (typeof document === "undefined") {
    return { loadingScreen: null, loadingError: null }
  }
  const loadingScreen = document.getElementById("loading-screen")
  const loadingError = document.getElementById("loading-error")
  return { loadingScreen, loadingError }
}

const showLoadingScreenOverlay = () => {
  const { loadingScreen } = getLoadingElements()
  if (loadingScreen) {
    loadingScreen.classList.remove("hidden")
    loadingScreen.style.opacity = ""
  }
  if (typeof document !== "undefined" && document.body) {
    document.body.classList.remove("app-ready")
  }
}

const hideLoadingScreenOverlay = () => {
  const { loadingScreen, loadingError } = getLoadingElements()
  if (loadingScreen) {
    loadingScreen.classList.add("hidden")
  }
  if (loadingError) {
    loadingError.style.display = "none"
    loadingError.textContent = ""
  }
  if (typeof document !== "undefined" && document.body) {
    document.body.classList.add("app-ready")
  }
}

const showLoadingScreenMessage = (message, { isError = false } = {}) => {
  showLoadingScreenOverlay()
  const { loadingError } = getLoadingElements()
  if (loadingError && message) {
    loadingError.textContent = message
    loadingError.style.display = "block"
  } else if (loadingError && !message) {
    loadingError.style.display = "none"
    loadingError.textContent = ""
  }
  if (isError && typeof window !== "undefined") {
    window.caseblocksHasCriticalError = true
  }
}

const extractErrorMessage = (error) => {
  if (!error) {
    return ""
  }
  if (typeof error === "string") {
    return error
  }
  if (error.message) {
    return error.message
  }
  if (error.reason) {
    return typeof error.reason === "string" ? error.reason : error.reason.message
  }
  return String(error)
}

const isNetworkError = (error) => {
  const message = extractErrorMessage(error)
  if (!message) {
    return false
  }
  return ["Failed to fetch", "Network error", "ERR_CONNECTION_REFUSED"].some((pattern) =>
    message.includes(pattern)
  )
}

// Extract URL information from various error types
const extractRequestUrl = (error, event) => {
  // Try to extract URL from error object
  if (error && error.config && error.config.url) {
    // Axios error
    return error.config.url
  }
  if (error && error.request && error.request.responseURL) {
    // XMLHttpRequest error
    return error.request.responseURL
  }
  if (error && error.url) {
    // Fetch error with url property
    return error.url
  }
  if (error && error.source) {
    // Error from a script or resource
    return error.source
  }
  if (event && event.target) {
    // Error event with target (e.g., script, img, link tags)
    if (event.target.src) {
      return event.target.src
    }
    if (event.target.href) {
      return event.target.href
    }
  }
  if (event && event.filename) {
    // Error event with filename
    return event.filename
  }
  // Check if error message contains a URL pattern
  const message = extractErrorMessage(error)
  if (message) {
    const urlMatch = message.match(/https?:\/\/[^\s]+/i)
    if (urlMatch) {
      return urlMatch[0]
    }
  }
  return null
}

const redirectToHome = () => {
  if (typeof window !== "undefined") {
    window.location.href = "/xg/"
  }
}

// Track if update prompt is already showing to prevent multiple prompts
let updatePromptShowing = false

// eslint-disable-next-line no-unused-vars
const showUpdatePrompt = () => {
  // Prevent showing multiple prompts
  if (updatePromptShowing || typeof document === "undefined") {
    return
  }
  updatePromptShowing = true

  // Create modal overlay
  const overlay = document.createElement("div")
  overlay.id = "update-prompt-overlay"
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 10000;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `

  // Create modal dialog
  const dialog = document.createElement("div")
  dialog.style.cssText = `
    background: white;
    border-radius: 8px;
    padding: 24px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  `

  // Create title
  const title = document.createElement("h2")
  title.textContent = "Application Update Available"
  title.style.cssText = `
    margin: 0 0 16px 0;
    font-size: 20px;
    font-weight: 600;
    color: #333;
  `

  // Create message
  const message = document.createElement("p")
  message.textContent = "A new version of the application is available. Please save any work you have open, then refresh to get the latest updates."
  message.style.cssText = `
    margin: 0 0 24px 0;
    font-size: 14px;
    line-height: 1.5;
    color: #666;
  `

  // Create button container
  const buttonContainer = document.createElement("div")
  buttonContainer.style.cssText = `
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  `

  // Create "Wait" button
  const waitButton = document.createElement("button")
  waitButton.textContent = "Wait"
  waitButton.style.cssText = `
    padding: 10px 20px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #333;
  `
  waitButton.onmouseover = () => {
    waitButton.style.background = "#e8e8e8"
  }
  waitButton.onmouseout = () => {
    waitButton.style.background = "#f5f5f5"
  }
  waitButton.onclick = () => {
    document.body.removeChild(overlay)
    updatePromptShowing = false
    // Show prompt again after 30 seconds if user hasn't refreshed
    setTimeout(() => {
      if (!updatePromptShowing) {
        showUpdatePrompt()
      }
    }, 30000)
  }

  // Create "Refresh Now" button
  const refreshButton = document.createElement("button")
  refreshButton.textContent = "Refresh Now"
  refreshButton.style.cssText = `
    padding: 10px 20px;
    background: #faa929;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: white;
  `
  refreshButton.onmouseover = () => {
    refreshButton.style.background = "#e89a19"
  }
  refreshButton.onmouseout = () => {
    refreshButton.style.background = "#faa929"
  }
  refreshButton.onclick = () => {
    window.location.reload()
  }

  // Assemble dialog
  buttonContainer.appendChild(waitButton)
  buttonContainer.appendChild(refreshButton)
  dialog.appendChild(title)
  dialog.appendChild(message)
  dialog.appendChild(buttonContainer)
  overlay.appendChild(dialog)

  // Add to page
  document.body.appendChild(overlay)
}

const startApiHealthCheck = () => {
  if (typeof window === "undefined") {
    return
  }
  if (apiHealthCheckIntervalId !== null) {
    return
  }

  const pingApi = async () => {
    try {
      const response = await fetch(GRAPHQL_HEALTHCHECK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          query: "query HealthCheck { __typename }"
        })
      })
      if (response.ok) {
        if (apiHealthCheckIntervalId !== null) {
          clearInterval(apiHealthCheckIntervalId)
          apiHealthCheckIntervalId = null
        }
        window.caseblocksHasCriticalError = false
        redirectToHome()
      }
    } catch (error) {
      console.warn("API health check failed", error && error.message ? error.message : error)
    }
  }

  pingApi()
  apiHealthCheckIntervalId = window.setInterval(pingApi, API_HEALTHCHECK_INTERVAL_MS)
}

const showNetworkErrorAndPoll = () => {
  showLoadingScreenMessage(LOADING_MESSAGES.network, { isError: true })
  startApiHealthCheck()
}

if (typeof window !== "undefined" && typeof window.caseblocksHasCriticalError === "undefined") {
  window.caseblocksHasCriticalError = false
}

// Utility function to sanitize and truncate large objects before sending to Sentry
// This prevents log entries from exceeding Google Cloud Logging's 256KB limit
// Uses more aggressive limits to ensure we stay well under the limit
const sanitizeForSentry = (obj, maxDepth = 3, currentDepth = 0, maxStringLength = 500) => {
  if (obj === null || obj === undefined) {
    return obj
  }
  
  // Prevent infinite recursion - more aggressive depth limit
  if (currentDepth >= maxDepth) {
    return "[Max depth reached]"
  }
  
  // Handle primitives
  if (typeof obj === "string") {
    if (obj.length > maxStringLength) {
      return obj.substring(0, maxStringLength) + `...[truncated ${obj.length - maxStringLength} chars]`
    }
    return obj
  }
  
  if (typeof obj !== "object") {
    return obj
  }
  
  // Handle arrays - more aggressive limit
  if (Array.isArray(obj)) {
    const maxArrayLength = 20 // Reduced from 50
    const truncated = obj.length > maxArrayLength
    const items = obj.slice(0, maxArrayLength).map(item => 
      sanitizeForSentry(item, maxDepth, currentDepth + 1, maxStringLength)
    )
    if (truncated) {
      items.push(`...[truncated ${obj.length - maxArrayLength} items]`)
    }
    return items
  }
  
  // Handle objects - more aggressive limit
  const sanitized = {}
  let keyCount = 0
  const maxKeys = 50 // Reduced from 100
  
  for (const key in obj) {
    if (keyCount >= maxKeys) {
      sanitized["...[truncated keys]"] = `[${Object.keys(obj).length - maxKeys} more keys]`
      break
    }
    
    try {
      // Skip circular references and functions
      if (typeof obj[key] === "function") {
        sanitized[key] = "[Function]"
      } else {
        sanitized[key] = sanitizeForSentry(obj[key], maxDepth, currentDepth + 1, maxStringLength)
      }
    } catch (e) {
      sanitized[key] = "[Error serializing]"
    }
    keyCount++
  }
  
  return sanitized
}

// Estimate the size of an object in bytes (rough approximation)
const estimateObjectSize = (obj) => {
  try {
    return JSON.stringify(obj).length
  } catch (e) {
    return 0
  }
}

window.version = require("./VERSION.json").version
console.log(`Caseblocks GUI - Version ${window.version}`)

Vue.component(Verte.name, Verte)
Vue.component("font-awesome-icon", FontAwesomeIcon)

Vue.use(wysiwyg, {
  hideModules: {
    image: true,
    table: true,
  },
})

Vue.use(VueInputAutoWidth)
Vue.use(currency)
// you can set default global options and events when use

const store = createStore(provider, router)
sync(store, router)

const cbConfig = window.localStorage.getItem("cb-config")
if (!cbConfig) {
  window.localStorage.setItem("cb-config", JSON.stringify(cfg.default()))
}

Vue.use(Vuetify, {
  theme: {
    primary: "#101b23",
    secondary: "#1d303e",
    tertiary: "#faa929",
    accent: "#0d9460",
    error: "#db444d",
    warning: "#f19916",
    attention: "#f7931e",
    info: "#09a1b0",
    success: "#0d9460",
    background: "#eae8e6",
    cardBackground: "#ffffff",
    cardBackgroundWhite: "#eeeeee",

    blue: "#1d303e",
    brightBlue: "#00526c",
    lightBlue: "#97b9e1",
    pink: "#e09995",
    purple: "#784579",
    yellow: "#faa929",
    green: "#46b379",
    orange: "#e0662f",
  },
})

Vue.use(Vuebar)
Vue.config.productionTip = false
Vue.prototype.$provider = provider

/* eslint-disable no-new */
const app = new Vue({
  el: "#app",
  router,
  components: {
    App,
  },
  template: "<App/>",
  store,
  methods: {
    dismissLoadingOverlay() {
      if (this._loadingScreenDismissed) {
        return
      }
      hideLoadingScreenOverlay()
      this._loadingScreenDismissed = true
    }
  },
  mounted() {
    this._loadingScreenDismissed = false
    this._hasObservedLoadingStart = false
    let initialWatchRun = true

    this.$store.watch(
      (state) => state.ui.loading,
      (loading) => {
        if (initialWatchRun) {
          initialWatchRun = false
          if (!loading) {
            return
          }
        }

        if (loading) {
          this._hasObservedLoadingStart = true
          return
        }

        const hasCriticalError = typeof window !== "undefined" ? window.caseblocksHasCriticalError : false
        if (!this._hasObservedLoadingStart || hasCriticalError) {
          return
        }

        this.dismissLoadingOverlay()
      },
      { immediate: true }
    )

    this._noLoadingActivityTimer = window.setTimeout(() => {
      const hasCriticalError = typeof window !== "undefined" ? window.caseblocksHasCriticalError : false
      if (!this._loadingScreenDismissed && !hasCriticalError) {
        this.dismissLoadingOverlay()
      }
    }, 2000)

    this._loadingDelayWarningTimer = window.setTimeout(() => {
      const hasCriticalError = typeof window !== "undefined" ? window.caseblocksHasCriticalError : false
      if (this._loadingScreenDismissed || hasCriticalError) {
        return
      }
      showLoadingScreenMessage("Still loading Caseblocks... please wait a moment.")
    }, 10000)
  },
  beforeDestroy() {
    if (this._noLoadingActivityTimer) {
      clearTimeout(this._noLoadingActivityTimer)
    }
    if (this._loadingDelayWarningTimer) {
      clearTimeout(this._loadingDelayWarningTimer)
    }
  },
  errorCaptured(err, vm, info) {
    // Log errors but don't show error page for every error
    if (err && err.message && !err.message.includes('Warning')) {
      console.error('Vue Error:', err, info)
      
      // Extract request URL if this is a network error
      const requestUrl = extractRequestUrl(err, null)
      
      // Send to Sentry for monitoring
      // Sanitize extra data to prevent large payloads
      const extraData = {
        componentInfo: vm
          ? {
              name: vm.$options.name,
              path: vm.$options.__file
            }
          : null,
        errorInfo: sanitizeForSentry(info)
      }
      
      Sentry.captureException(err, {
        tags: {
          errorType: 'vue_error',
          component: vm
            ? vm.$options.name || 'unknown'
            : 'unknown',
          url: window.location.href,
          ...(requestUrl && { requestUrl: requestUrl })
        },
        extra: {
          componentInfo: vm
            ? {
                name: vm.$options.name,
                path: vm.$options.__file
              }
            : null,
          errorInfo: info,
          ...(requestUrl && { requestUrl: requestUrl })
        }
      })
      
      // Only show error page for truly critical errors that break the app
      // Most component errors can be handled gracefully without showing the error page
      // Uncomment the line below if you want to show error page for Vue errors:
      // showLoadingScreenMessage(LOADING_MESSAGES.generic, { isError: true })
    }
    return false // Prevent error from propagating
  }
})

if (window.Cypress) {
  window.app = app
}

console.log("Setting up sentry for", Helpers.currentEnvironment())
Sentry.init({
  Vue,
  dsn: "https://50c21a08d1964da2c1030ad2a7a01e84@o4507072991461376.ingest.de.sentry.io/4507861809954896",
  integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],

  environment: Helpers.currentEnvironment(),
  // Tracing - halved again (was 0.025) to reduce span volume
  tracesSampleRate: 0.0125,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/[a-zA-Z0-9]+.caseblocks.com/, /^http:\/\/[a-zA-Z0-9]+.staging.caseblocks.com/],

  // Session replay - reduced by 90%. replaysOnErrorSampleRate stays 1.0 to capture replays when errors occur
  replaysSessionSampleRate: process.env.NODE_ENV === 'production'
    ? 0.01
    : 0.005,
  replaysOnErrorSampleRate: 1.0,

  // Profiling sample rate; halved with tracesSampleRate
  profilesSampleRate: process.env.NODE_ENV === 'production' ? 0.005 : 0.01,
  
  // Enable debug mode in development
  // debug: process.env.NODE_ENV !== 'production',
  
  // Capture all errors and exceptions
  beforeSend(event) {
    // Filter out email body content errors - these are expected and not real errors
    if (event.exception && event.exception.values) {
      const exception = event.exception.values[0]
      const errorValue = exception.value || ''
      const errorMessage = String(errorValue)
      
      // Check if this is an email body content error
      if (errorMessage.includes('Invalid or unexpected token') ||
          errorMessage.includes('missing )') ||
          errorMessage.includes('Unexpected token')) {
        // Check the source/URL to confirm it's from email body
        const stacktrace = exception.stacktrace || {}
        const frames = stacktrace.frames || []
        const firstFrame = frames[0] || {}
        const errorSource = firstFrame.filename || 
                           firstFrame.abs_path || 
                           (event.request && event.request.url) || ''
        if (errorSource.includes('/body?token=') ||
            errorSource.includes('/commsroom/messages/') ||
            errorSource.includes('body')) {
          // Don't send email body content errors to Sentry
          return null
        }
      }
    }
    
    // Ensure request URL is always in a prominent location for network errors
    // The URL will be available in:
    // 1. event.tags.requestUrl (if set)
    // 2. event.extra.requestUrl (if set)
    // 3. event.request.url (for HTTP request context)
    // 4. event.contexts.request.url (for browser request context)
    if (event.extra && event.extra.requestUrl) {
      // Also add to contexts for better visibility
      if (!event.contexts) {
        event.contexts = {}
      }
      if (!event.contexts.request) {
        event.contexts.request = {}
      }
      if (!event.contexts.request.url) {
        event.contexts.request.url = event.extra.requestUrl
      }
    }
    
    // Always capture errors in production (except email body errors above)
    if (process.env.NODE_ENV === 'production') {
      return event
    }
    // In development, filter out some noise
    if (event.exception && event.exception.values) {
      const exception = event.exception.values[0]
      if (exception.value && (
        exception.value.includes('ResizeObserver') ||
        exception.value.includes('Script error') ||
        exception.value.includes('Network Error')
      )) {
        return null
      }
    }
    return event
  }
})

Vue.router = router
Vue.store = store
try {
  if (Faye) {
    window.faye = new Faye.Client("/faye")
  }
} catch (error) {
  // Ignore silently
}

window.env = process.env.NODE_ENV

// Global error handler for unhandled errors
window.addEventListener("error", function(event) {
  // Filter out errors from email body content - these are expected and should be silently caught
  const errorSource = event.filename || event.source || (event.target && event.target.src) || ''
  const errorMessage = (event.error && event.error.message) || event.message || (event.error && event.error.toString()) || ''
  const errorString = String(errorMessage || '')
  
  // Check if this is an error from email body content
  // Look for various patterns that indicate email body content errors
  const isEmailBodyError = (
    errorSource.includes('/body?token=') ||
    errorSource.includes('/commsroom/messages/') ||
    errorSource.includes('body?token=') ||
    (errorString.includes('Invalid or unexpected token') && (
      errorSource.includes('body') || 
      errorSource.includes('commsroom') ||
      errorSource.includes('messages')
    )) ||
    (errorString.includes('missing )') && errorSource.includes('body')) ||
    (errorString.includes('Unexpected token') && errorSource.includes('body'))
  )
  
  if (isEmailBodyError) {
    // Silently catch email body content errors - these are from email content, not our code
    // Don't log to console to avoid noise
    event.preventDefault() // Prevent error from showing in console
    event.stopPropagation() // Stop error from propagating
    return false // Don't propagate the error
  }

  console.error("Global Error:", event.error)

  // Extract request URL from the error
  const requestUrl = extractRequestUrl(event.error, event) || errorSource || null

  Sentry.captureException(event.error, {
    tags: {
      errorType: "global_error",
      url: window.location.href,
      ...(requestUrl && { requestUrl: requestUrl })
    },
    extra: {
      errorEvent: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error ? {
          message: event.error.message,
          stack: event.error.stack,
          name: event.error.name
        } : null
      },
      ...(requestUrl && { requestUrl: requestUrl })
    }
  })

  // Handle ChunkLoadError - this occurs when old HTML references chunks that no longer exist
  // after a new deployment. Reload the page to get fresh HTML and assets.
  if (event.error) {
    const isChunkLoadError = (
      errorMessage.includes('ChunkLoadError') ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('Failed to fetch dynamically imported module')
    )
    
    if (isChunkLoadError) {
      console.log('ChunkLoadError detected - reloading page to get latest assets')
      // Use location.reload() to get fresh HTML and assets without hard refresh
      window.location.reload()
    }
  }
})

window.addEventListener("unhandledrejection", function(event) {
  const errorMessage = (event.reason && event.reason.message) || (event.reason && event.reason.toString()) || ''
  const errorName = (event.reason && event.reason.name) || ''
  
  // Handle NavigationDuplicated errors - these are harmless when navigating to the same route
  if (errorName === 'NavigationDuplicated' || errorMessage.includes('NavigationDuplicated')) {
    // Silently ignore - this is expected behavior when trying to navigate to current route
    event.preventDefault()
    return
  }
  
  // Handle navigation guard redirect errors - these are expected and should be warnings
  if (errorMessage.includes("Redirected when going from") && errorMessage.includes("via a navigation guard")) {
    console.warn("Navigation guard redirect (silently handled):", errorMessage)
    event.preventDefault() // Prevent error from showing in console
    return
  }

  console.error("Unhandled Promise Rejection:", event.reason)

  // Extract request URL from the rejection reason
  const requestUrl = extractRequestUrl(event.reason, null) || (event.reason && event.reason.requestUrl) || null
  
  // Check if this is an HTML response error (server returned HTML instead of JSON)
  const isHtmlResponseError = errorMessage.includes("Unexpected token '<'") ||
                             errorMessage.includes("HTML instead of JSON") ||
                             errorMessage.includes('<!DOCTYPE') ||
                             (event.reason && event.reason.name === 'HTMLResponseError')

  Sentry.captureException(event.reason, {
    tags: {
      errorType: isHtmlResponseError ? "html_response_error" : "unhandled_rejection",
      url: window.location.href,
      ...(requestUrl && { requestUrl: requestUrl }),
      ...(isHtmlResponseError && { htmlResponseDetected: "true" })
    },
    extra: {
      promiseEvent: {
        reason: event.reason ? {
          message: event.reason.message,
          stack: event.reason.stack,
          name: event.reason.name
        } : null
      },
      ...(requestUrl && { requestUrl: requestUrl }),
      ...(event.reason && event.reason.statusCode && { statusCode: event.reason.statusCode }),
      ...(event.reason && event.reason.statusText && { statusText: event.reason.statusText }),
      ...(event.reason && event.reason.responseBody && { responseBodySnippet: event.reason.responseBody }),
      ...(isHtmlResponseError && {
        htmlResponseDetected: true,
        note: "Server returned HTML instead of JSON. Check requestUrl tag/extra for the endpoint that failed."
      })
    }
  })
})

window.addEventListener("offline", () => {
  showNetworkErrorAndPoll()
})
